import type { ServicePageData } from "$lib/types/service-detail";

const ecommerceRetouchingMedia = {
  heroPackshot: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-hero-skincare-serum-zen-stones.webp",
    alt: "Misolo skincare serum, cream, and dropper bottles styled with zen stones",
    width: 1600,
    height: 2000,
  },
  heroFootwear: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-hero-pointed-flats-footwear.webp",
    alt: "Pair of ivory pointed flats with textured toes prepared for an e-commerce catalog",
    width: 1122,
    height: 1402,
  },
  heroWatch: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-hero-turquoise-handbag-perfume-flatlay.webp",
    alt: "Turquoise quilted handbag and perfume styled together for an e-commerce flat lay",
    width: 1600,
    height: 2000,
  },
  introCatalogBoard: {
    src: "/videos/ecommerce-retouching-intro-workflow.mp4",
    alt: "E-commerce product and fashion model retouching workflow preview video",
    width: 1080,
    height: 1350,
  },
  comparisonOriginal: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-comparison-model-denim-before.webp",
    alt: "Fashion model wearing a denim outfit before e-commerce model retouching",
    width: 1333,
    height: 2000,
  },
  comparisonRetouched: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-comparison-model-denim-after.webp",
    alt: "Fashion model wearing a denim outfit after e-commerce model retouching",
    width: 1333,
    height: 2000,
  },
  productBefore: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-showcase-gold-bag-before.webp",
    alt: "Metallic gold handbag before product retouching",
    width: 1500,
    height: 1875,
  },
  productAfter: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-showcase-gold-bag-after.webp",
    alt: "Metallic gold handbag after product retouching",
    width: 1500,
    height: 1875,
  },
  shadowBefore: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-shadow-bottle-before.webp",
    alt: "Ricin bio bottle before drop shadow retouching",
    width: 1600,
    height: 2000,
  },
  shadowAfter: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-shadow-bottle-after.webp",
    alt: "Ricin bio bottle after drop shadow retouching",
    width: 1600,
    height: 2000,
  },
  packshotBefore: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-packshot-commercial-before.webp",
    alt: "Commercial product packshot before high-end retouching",
    width: 1600,
    height: 2000,
  },
  packshotAfter: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-packshot-commercial-after.webp",
    alt: "Commercial product packshot after high-end retouching",
    width: 1600,
    height: 2000,
  },
  maskingBefore: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-masking-fashion-jacket-before.webp",
    alt: "Fashion jacket before image masking and edge isolation",
    width: 1600,
    height: 2000,
  },
  maskingAfter: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-masking-fashion-jacket-after.webp",
    alt: "Fashion jacket after image masking and edge isolation",
    width: 1600,
    height: 2000,
  },
  luxuryWatchBefore: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-comparison-luxury-watch-before.webp",
    alt: "Luxury wristwatch before high-end commercial retouching and reflection balancing",
    width: 1600,
    height: 2000,
  },
  luxuryWatchAfter: {
    src: "/images/ecommerce-retouching/ecommerce-retouching-comparison-luxury-watch-after.webp",
    alt: "Luxury wristwatch after high-end commercial retouching, crystal cleanup, and dial enhancement",
    width: 1600,
    height: 2000,
  },
} as const;

export const ecommerceRetouchingPage: ServicePageData = {
  slug: "ecommerce-retouching",
  seo: {
    title: "E-commerce Product Photo Retouching | Studio Click House",
    description:
      "Product photo retouching for e-commerce catalogs: dust and scratch cleanup, background preparation, color matching, and consistent crops across product images.",
  },
  hero: {
    title: "E-commerce",
    titleAccent: "Retouching.",
    theme: "light",
    titleWidth: "wide",
    mediaFit: "cover",
    aspectRatio: "4/5",
    description:
      "High-capacity, high-conversion product retouching built for Amazon, Shopify, eBay, and luxury online retailers. We eliminate dust, scratches, and inconsistencies to deliver uniform, pristine catalog listings overnight.",
    media: ecommerceRetouchingMedia.heroPackshot,
    supportingMedia: [
      ecommerceRetouchingMedia.heroFootwear,
      ecommerceRetouchingMedia.heroWatch,
    ],
  },
  intro: {
    heading: "What is professional e-commerce retouching?",
    paragraphs: [
      "E-commerce retouching cleans dust, sensor spots, scuffs, creases, and unwanted reflections while standardizing crop, margins, and color across product SKUs. Our multi-shift production team supports large catalog batches and agreed deadlines.",
    ],
    stages: [
      {
        label: "Sub-Pixel Dust & Scuff Removal",
        description:
          "We clean product surfaces, remove lens flare artifacts, wipe away fingerprints, and repair micro-scratches.",
        media: ecommerceRetouchingMedia.introCatalogBoard,
      },
      {
        label: "Marketplace Alignment & Margins",
        description:
          "Images are aligned to uniform vertical axes with standardized 85% image fills and pure white (#FFFFFF) backdrops.",
      },
      {
        label: "True-Color Swatch Verification",
        description:
          "We cross-reference product hues against physical color swatches to prevent customer returns caused by color discrepancy.",
      },
    ],
  },
  beforeAfter: {
    heading: "E-commerce model retouching",
    description:
      "Compare the original fashion image with its polished catalog version, keeping the garment and model presentation consistent.",
    bullets: [
      "Preserve natural skin texture and garment detail",
      "Refine folds, fit, and color for catalog consistency",
      "Keep model imagery cohesive across product listings",
    ],
    beforeSrc: ecommerceRetouchingMedia.comparisonOriginal.src,
    beforeAlt: ecommerceRetouchingMedia.comparisonOriginal.alt,
    afterSrc: ecommerceRetouchingMedia.comparisonRetouched.src,
    afterAlt: ecommerceRetouchingMedia.comparisonRetouched.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    width: ecommerceRetouchingMedia.comparisonRetouched.width,
    height: ecommerceRetouchingMedia.comparisonRetouched.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "E-commerce product retouching",
    description:
      "Clean product surfaces, refine metallic finishes, and preserve material detail for consistent e-commerce catalogs.",
    bullets: [
      "Remove surface marks while preserving material texture",
      "Refine metallic highlights and product color",
      "Keep catalog framing and presentation consistent",
    ],
    theme: "light",
    beforeAfter: {
      before: { ...ecommerceRetouchingMedia.productBefore, label: "Before" },
      after: { ...ecommerceRetouchingMedia.productAfter, label: "After" },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "ecommerce-retouching-shadow-comparison",
      heading: "Natural drop shadow retouching",
      description:
        "Add a soft, realistic grounding shadow to product packshots while keeping the bottle, label, and background clean.",
      bullets: [
        "Add a soft, realistic contact shadow",
        "Keep the bottle and label details clear",
        "Ground the product without distracting from it",
      ],
      beforeSrc: ecommerceRetouchingMedia.shadowBefore.src,
      beforeAlt: ecommerceRetouchingMedia.shadowBefore.alt,
      afterSrc: ecommerceRetouchingMedia.shadowAfter.src,
      afterAlt: ecommerceRetouchingMedia.shadowAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: ecommerceRetouchingMedia.shadowAfter.width,
      height: ecommerceRetouchingMedia.shadowAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "ecommerce-retouching-high-end-comparison",
      heading: "High-end product photo retouching",
      description:
        "Polish commercial packshots with careful cleanup, balanced lighting, and refined detail while preserving the product’s natural form.",
      bullets: [
        "Balance highlights and surface detail",
        "Clean minor marks and distracting reflections",
        "Preserve the product’s natural shape and color",
      ],
      beforeSrc: ecommerceRetouchingMedia.packshotBefore.src,
      beforeAlt: ecommerceRetouchingMedia.packshotBefore.alt,
      afterSrc: ecommerceRetouchingMedia.packshotAfter.src,
      afterAlt: ecommerceRetouchingMedia.packshotAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: ecommerceRetouchingMedia.packshotAfter.width,
      height: ecommerceRetouchingMedia.packshotAfter.height,
      layout: "cards",
      textPosition: "left",
    },
    {
      sectionId: "ecommerce-retouching-image-masking-comparison",
      heading: "Precise image masking",
      description:
        "Isolate the jacket with clean, accurate edges so it can be placed on a new background or prepared for catalog use.",
      bullets: [
        "Trace accurate edges around the jacket silhouette",
        "Preserve fine fabric details during isolation",
        "Prepare a clean cutout for new backgrounds",
      ],
      beforeSrc: ecommerceRetouchingMedia.maskingBefore.src,
      beforeAlt: ecommerceRetouchingMedia.maskingBefore.alt,
      afterSrc: ecommerceRetouchingMedia.maskingAfter.src,
      afterAlt: ecommerceRetouchingMedia.maskingAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: ecommerceRetouchingMedia.maskingAfter.width,
      height: ecommerceRetouchingMedia.maskingAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "ecommerce-retouching-luxury-watch-comparison",
      heading: "High-end luxury watch & timepiece retouching",
      description:
        "Refine luxury watch photography by removing sapphire crystal glare, polishing metallic bezels and bracelets, aligning hands, and enhancing dial typography with microscopic precision.",
      bullets: [
        "Eliminate distracting glare and dust particles from the sapphire crystal",
        "Balance metallic bevel highlights and brushed steel textures",
        "Enhance dial numerals, sub-dials, and luminous marker contrast",
      ],
      beforeSrc: ecommerceRetouchingMedia.luxuryWatchBefore.src,
      beforeAlt: ecommerceRetouchingMedia.luxuryWatchBefore.alt,
      afterSrc: ecommerceRetouchingMedia.luxuryWatchAfter.src,
      afterAlt: ecommerceRetouchingMedia.luxuryWatchAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: ecommerceRetouchingMedia.luxuryWatchAfter.width,
      height: ecommerceRetouchingMedia.luxuryWatchAfter.height,
      layout: "cards",
      textPosition: "left",
    },
  ],
  productTypes: [
    {
      id: "pink-chalk-shorts",
      code: "01",
      title: "Pink Chalk Shorts",
      subtitle: "Ghost mannequin apparel with balanced shape and clean fabric detail.",
      src: "/images/ecommerce-retouching/ecommerce-retouching-product-pink-chalk-shorts.webp",
      alt: "Pink chalk shorts finished with a ghost mannequin presentation",
      fit: "cover",
    },
    {
      id: "white-leather-sneakers",
      code: "02",
      title: "White Leather Sneakers",
      subtitle: "Clean leather surfaces, seams, and edges for footwear catalogs.",
      src: "/images/ecommerce-retouching/ecommerce-retouching-product-white-leather-sneakers.webp",
      alt: "White leather sneakers prepared for an e-commerce catalog",
      fit: "cover",
    },
    {
      id: "designer-sunglasses",
      code: "03",
      title: "Designer Sunglasses",
      subtitle: "Preserve frame shape, lens tint, and controlled reflections.",
      src: "/images/ecommerce-retouching/ecommerce-retouching-product-designer-sunglasses.webp",
      alt: "Designer sunglasses shown in side profile before retouching",
      fit: "cover",
    },
    {
      id: "black-trucker-hat",
      code: "04",
      title: "Black Trucker Hat",
      subtitle: "Show mesh, stitching, and brim shape with clear product detail.",
      src: "/images/ecommerce-retouching/ecommerce-retouching-product-black-trucker-hat.webp",
      alt: "Black trucker hat presented for an e-commerce product listing",
      fit: "cover",
    },
    {
      id: "skincare-cream-jars",
      code: "05",
      title: "Skincare Cream Jars",
      subtitle: "Refine packaging color and detail across a coordinated product set.",
      src: "/images/ecommerce-retouching/ecommerce-retouching-product-skincare-cream-jars.webp",
      alt: "Misolo skincare serum and bamboo cream jars arranged as a product trio",
      fit: "cover",
    },
    {
      id: "mens-tailored-coat",
      code: "06",
      title: "Men’s Tailored Coat",
      subtitle: "Present the coat silhouette and construction with natural shape.",
      src: "/images/ecommerce-retouching/ecommerce-retouching-product-mens-tailored-coat.webp",
      alt: "Men’s tailored coat finished with a ghost mannequin presentation",
      fit: "cover",
    },
  ],
  features: {
    heading: "Why e-commerce teams use Studio Click House",
    items: [
      {
        icon: "CheckCircle2",
        title: "Marketplace-ready exports",
        description:
          "100% compliant with Amazon, Shopify, Walmart, Target, and eBay specifications for pure white RGB(255,255,255) backdrops and aspect ratios.",
      },
      {
        icon: "Zap",
        title: "Overnight 24/7 Production Shifts",
        description:
          "Upload your daytime shoot files and receive retouched, catalog-formatted listings ready for upload before your morning coffee.",
      },
      {
        icon: "Sparkles",
        title: "Micro-Dust & Scratch Eradication",
        description:
          "We clean every surface, seam, and edge under high magnification so your products look brand-new and luxurious.",
      },
      {
        icon: "Maximize2",
        title: "Strict Sizing & Margin Uniformity",
        description:
          "Every product in a category receives identical alignment, margin percentage, and crop ratios for a clean, cohesive grid aesthetic.",
      },
      {
        icon: "ShieldCheck",
        title: "Two-Tier Quality Control",
        description:
          "Every batch passes an artist review and a senior QC director inspection before export to eliminate errors and revision lag.",
      },
      {
        icon: "BadgePercent",
        title: "Competitive High-Volume Pricing",
        description:
          "Tiered volume pricing designed to dramatically lower your per-SKU cost of content production while boosting conversion rates.",
      },
    ],
  },
  faqs: [
    {
      question:
        "How do you guarantee batch consistency across thousands of SKUs?",
      answer:
        "We establish custom brand style guides for every client, documenting exact crop percentages, margin sizes, shadow softness, and color profiles. Automated verification scripts and senior QC managers verify that every file adheres strictly to these parameters.",
    },
    {
      question:
        "What is your standard turnaround time for a 1,000-image batch?",
      answer:
        "Standard batches of 500 to 1,000 images are delivered within 24 to 36 hours. For ongoing enterprise partnerships, we allocate dedicated artist pods operating 24/7 to guarantee consistent daily throughput.",
    },
    {
      question: "Can you provide files formatted for multiple marketplaces?",
      answer:
        "Yes. We can deliver multiple export formats per SKU—such as square 2000x2000px on pure white for Amazon, 4:5 vertical crops for social storefronts, and transparent PNGs for banner compositing.",
    },
    {
      question:
        "Do you offer shadow creation as part of e-commerce retouching?",
      answer:
        "Yes, we can preserve the original studio shadow, create a soft drop shadow, or add an elegant reflection shadow beneath the product.",
    },
    {
      question: "How do we get started with a trial batch?",
      answer:
        "Simply send us 3 to 5 sample images along with your catalog guidelines. We will deliver free test edits within 24 hours so you can verify our quality firsthand.",
    },
  ],
  cta: {
    heading: "Scale your e-commerce catalog with zero friction",
    description:
      "Send us your sample images and catalog specifications. We will deliver a free trial edit and a custom volume rate within hours.",
    steps: [
      {
        title: "Send sample batch",
        description:
          "Upload up to 5 test images with your crop, margin, and background guidelines.",
      },
      {
        title: "Review trial listings",
        description:
          "Inspect our dust cleanup, white balance, and edge definition with zero commitment.",
      },
      {
        title: "Launch high-volume pipeline",
        description:
          "Keep seasonal catalog collections moving with agreed overnight turnaround windows.",
      },
    ],
  },
};
